import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { NAVY, GOLD } from '../utils/constants';

// ── Number to words (Indian system) ──────────────────────────────────────────
const ONES = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
  'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
  'Seventeen','Eighteen','Nineteen'];
const TENS = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

const cvtHundreds = (n) => {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n/10)] + (n%10 ? ' ' + ONES[n%10] : '');
  return ONES[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' and ' + cvtHundreds(n%100) : '');
};

const amountToWords = (amount) => {
  const n = Math.round(amount);
  if (n === 0) return 'Zero Rupees Only';
  const crore    = Math.floor(n / 10000000);
  const lakh     = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rem      = n % 1000;
  let result = '';
  if (crore)    result += cvtHundreds(crore)    + ' Crore ';
  if (lakh)     result += cvtHundreds(lakh)     + ' Lakh ';
  if (thousand) result += cvtHundreds(thousand) + ' Thousand ';
  if (rem)      result += cvtHundreds(rem);
  return 'Rupees ' + result.trim() + ' Only';
};

// ── Date helpers ──────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = (n) => { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };

const fmtYMD = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m-1]} ${y}`;
};
const fmtDMY = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m, y] = ddmmyyyy.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m-1]} ${y}`;
};

const f2  = (n) => Number(n || 0).toFixed(2);
const fI  = (n) => Number(Math.round(n || 0)).toLocaleString('en-IN');

// ── Reusable table primitives ─────────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '7px 10px',
      textAlign: align,
      fontWeight: 700,
      fontSize: 9.5,
      color: '#5a4a38',
      background: '#f3f0ea',
      borderBottom: '1.5px solid #d8d0c0',
      borderTop: '1.5px solid #d8d0c0',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  );
}

function Td({ children, align = 'left', bold, shade }) {
  return (
    <td style={{
      padding: '6px 10px',
      textAlign: align,
      fontSize: 10,
      color: NAVY,
      fontWeight: bold ? 700 : 400,
      borderBottom: '1px solid #f0ebe0',
      background: shade ? '#fafaf8' : 'transparent',
    }}>
      {children}
    </td>
  );
}

function GoldDivider() {
  return <div style={{ height: 2, background: GOLD, width: '100%' }} />;
}

function InfoRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 3, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 10, color: '#7a6a50', fontWeight: 600, minWidth: 88, flexShrink: 0 }}>{label}:</span>
      <span style={{ fontSize: 10, color: NAVY, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}

function TotalRow({ label, value, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: big ? '7px 0' : '4px 0', borderBottom: big ? 'none' : '1px solid #f0ebe0' }}>
      <span style={{ fontSize: big ? 12 : 10, color: big ? NAVY : '#5a4a38', fontWeight: big ? 800 : 600 }}>{label}</span>
      <span style={{ fontSize: big ? 13 : 10, color: big ? GOLD : NAVY, fontWeight: big ? 800 : 600 }}>₹{value}</span>
    </div>
  );
}

// ── Invoice content (forwardRef so useReactToPrint can access the DOM node) ──
const InvoiceContent = React.forwardRef(function InvoiceContent({ invoiceData }, ref) {
  const { invoiceNumber, invoiceDate, customerName, customerPhone, booking, settings, taxData, partner_gstin, partner_address, partner_state, partner_state_code, remarks, cruiseDisplayName } = invoiceData;

  // B2B (partner GST invoice): discount-first presentation. Every B2B branch is
  // guarded so B2C rendering is byte-for-byte identical to before.
  const isB2B = !!(taxData && taxData.isB2B) || !!invoiceData.isB2B;
  const tac       = Number(booking.tac) || 0;
  // Old post-total TAC deduction is replaced by the discount-first block for B2B.
  const hasTac    = tac > 0 && !!booking.partner_name && !isB2B;
  const netAmount = hasTac ? taxData.grandTotal - tac : null;

  // Item-table taxable rates: show the DISCOUNTED per-head taxable as the rate
  // (per CA — no "full rate less discount" presentation). For B2C the *Disc fields
  // equal the full values, so output is unchanged there.
  const itAdultCruise = taxData.adultCruiseTaxableDisc;
  const itChildCruise = taxData.childCruiseTaxableDisc;
  const itAdultFood   = taxData.adultFoodTaxableDisc;
  const itChildFood   = taxData.childFoodTaxableDisc;
  // Non-dinner Model 2: separate Adult/Child rows at DISCOUNTED per-head taxable.
  const itAdultNon = taxData.adultTaxableDisc;
  const itChildNon = taxData.childTaxableDisc;

  const companyName = settings?.company_name || 'Brahmaputra Cruise Private Limited';
  const address     = (settings?.address     || '').replace(/\n/g, ', ');
  const gstin       = settings?.gstin        || '';
  const phone       = settings?.phone        || '';
  const email       = settings?.email        || '';
  const sacCruise   = settings?.cruise_sac_code || '996415';
  const sacFood     = settings?.food_sac_code   || '996331';

  const paxParts = [
    booking.adults   > 0 ? `${booking.adults} Adults`   : null,
    booking.children > 0 ? `${booking.children} Children` : null,
  ].filter(Boolean);
  const paxLabel = paxParts.join(', ') || '—';

  const totalCgst = taxData.isDinner
    ? taxData.cgstCruise + taxData.cgstFood
    : taxData.cgst;
  const totalSgst = taxData.isDinner
    ? taxData.sgstCruise + taxData.sgstFood
    : taxData.sgst;

  return (
    <div ref={ref} style={{
      width: '210mm',
      minHeight: '297mm',
      background: '#ffffff',
      fontFamily: 'Helvetica, Arial, sans-serif',
      boxSizing: 'border-box',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      margin: '0 auto',
    }}>

      {/* ── Header ── */}
      <div style={{ background: NAVY, padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <img
            src="/alfresco-grand.png"
            alt="Alfresco Grand"
            style={{ height: 40, mixBlendMode: 'screen', display: 'block', marginBottom: 8 }}
          />
          <div style={{ color: GOLD, fontSize: 20, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase' }}>
            TAX INVOICE
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#ffffff', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{companyName}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>{address}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>GSTIN: {gstin}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>
            {phone && `Ph: ${phone}`}{email && phone ? ` | ${email}` : email}
          </div>
        </div>
      </div>

      {/* ── Invoice + Bill To ── */}
      <div style={{ display: 'flex', padding: '16px 22px 12px', gap: 24, borderBottom: '1px solid #e8e0d0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Invoice Details</div>
          <InfoRow label="Invoice No"      value={invoiceNumber} bold />
          <InfoRow label="Invoice Date"    value={fmtYMD(invoiceDate)} />
          <InfoRow label="Place of Supply" value="Assam (18)" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Bill To</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginBottom: 3 }}>{customerName}</div>
          {isB2B && partner_address && String(partner_address).split('\n').map((line, i) => (
            line.trim() ? <div key={i} style={{ fontSize: 10.5, color: '#4a5a7a', lineHeight: 1.4 }}>{line}</div> : null
          ))}
          {isB2B && partner_state && (
            <div style={{ fontSize: 10.5, color: '#4a5a7a', marginTop: 2 }}>
              {partner_state}{partner_state_code ? ` (${partner_state_code})` : ''}
            </div>
          )}
          {isB2B && partner_gstin && (
            <div style={{ fontSize: 10.5, color: NAVY, fontWeight: 700, marginTop: 2, marginBottom: 2 }}>GSTIN: {partner_gstin}</div>
          )}
          {customerPhone && <div style={{ fontSize: 10.5, color: '#4a5a7a' }}>{customerPhone}</div>}
        </div>
      </div>

      <GoldDivider />

      {/* ── Booking details ── */}
      <div style={{ padding: '12px 22px 10px' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Booking Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 40px' }}>
          <InfoRow label="Cruise"          value={cruiseDisplayName || booking.cruise_type} />
          <InfoRow label="Date of Cruise"  value={fmtDMY(booking.date)} />
          <InfoRow label="Deck"            value={booking.deck} />
          <InfoRow label="Passengers"      value={paxLabel} />
        </div>
        {remarks && remarks.trim() && (
          <div style={{ marginTop: 4 }}>
            <InfoRow label="Remarks" value={remarks} />
          </div>
        )}
      </div>

      <GoldDivider />

      {/* ── Itemized table ── */}
      <div style={{ padding: '14px 22px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th align="left">Description</Th>
              <Th align="center">SAC</Th>
              <Th align="center">Qty</Th>
              <Th align="right">Rate (excl. GST)</Th>
              <Th align="right">Amount</Th>
            </tr>
          </thead>
          <tbody>
            {taxData.isDinner ? (
              <>
                {taxData.adults > 0 && (
                  <tr>
                    <Td>Cruise Charges</Td>
                    <Td align="center">{sacCruise}</Td>
                    <Td align="center">{taxData.adults}</Td>
                    <Td align="right">₹{f2(itAdultCruise)}</Td>
                    <Td align="right" bold>₹{f2(taxData.adults * itAdultCruise)}</Td>
                  </tr>
                )}
                {taxData.children > 0 && (
                  <tr>
                    <Td shade>Cruise Charges (Child)</Td>
                    <Td align="center" shade>{sacCruise}</Td>
                    <Td align="center" shade>{taxData.children}</Td>
                    <Td align="right" shade>₹{f2(itChildCruise)}</Td>
                    <Td align="right" bold shade>₹{f2(taxData.children * itChildCruise)}</Td>
                  </tr>
                )}
                {taxData.adults > 0 && (
                  <tr>
                    <Td>Food &amp; Beverage</Td>
                    <Td align="center">{sacFood}</Td>
                    <Td align="center">{taxData.adults}</Td>
                    <Td align="right">₹{f2(itAdultFood)}</Td>
                    <Td align="right" bold>₹{f2(taxData.adults * itAdultFood)}</Td>
                  </tr>
                )}
                {taxData.children > 0 && (
                  <tr>
                    <Td shade>Food &amp; Beverage (Child)</Td>
                    <Td align="center" shade>{sacFood}</Td>
                    <Td align="center" shade>{taxData.children}</Td>
                    <Td align="right" shade>₹{f2(itChildFood)}</Td>
                    <Td align="right" bold shade>₹{f2(taxData.children * itChildFood)}</Td>
                  </tr>
                )}
              </>
            ) : (
              <>
                {taxData.adults > 0 && (
                  <tr>
                    <Td>Cruise Charges (Adult)</Td>
                    <Td align="center">{sacCruise}</Td>
                    <Td align="center">{taxData.adults}</Td>
                    <Td align="right">₹{f2(itAdultNon)}</Td>
                    <Td align="right" bold>₹{f2(taxData.adults * itAdultNon)}</Td>
                  </tr>
                )}
                {taxData.children > 0 && (
                  <tr>
                    <Td shade>Cruise Charges (Child)</Td>
                    <Td align="center" shade>{sacCruise}</Td>
                    <Td align="center" shade>{taxData.children}</Td>
                    <Td align="right" shade>₹{f2(itChildNon)}</Td>
                    <Td align="right" bold shade>₹{f2(taxData.children * itChildNon)}</Td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Partner discount block removed per CA: item rates are already discounted, so
             this bridge never renders (showDiscountBlock is always false). ── */}
      {taxData.showDiscountBlock && (
        <div style={{ padding: '12px 22px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0ebe0' }}>
              <span style={{ fontSize: 10, color: '#5a4a38', fontWeight: 600 }}>Gross Taxable</span>
              <span style={{ fontSize: 10, color: NAVY, fontWeight: 600 }}>₹{f2(taxData.taxableTotalFull)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0ebe0' }}>
              <span style={{ fontSize: 10, color: '#5a4a38', fontWeight: 600 }}>Less: Partner Discount</span>
              <span style={{ fontSize: 10, color: '#c0392b', fontWeight: 700 }}>-₹{f2(taxData.discountTaxable)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span style={{ fontSize: 10.5, color: NAVY, fontWeight: 700 }}>Net Taxable</span>
              <span style={{ fontSize: 10.5, color: NAVY, fontWeight: 700 }}>₹{f2(taxData.taxableTotal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Tax summary ── */}
      <div style={{ padding: '12px 22px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th align="left">Description</Th>
              <Th align="right">Taxable Amount</Th>
              <Th align="center">CGST %</Th>
              <Th align="right">CGST Amt</Th>
              <Th align="center">SGST %</Th>
              <Th align="right">SGST Amt</Th>
              <Th align="right">Total Tax</Th>
            </tr>
          </thead>
          <tbody>
            {taxData.isDinner ? (
              <>
                <tr>
                  <Td>Cruise Charges</Td>
                  <Td align="right">₹{f2(taxData.taxableCruise)}</Td>
                  <Td align="center">{taxData.cgstCruisePct}%</Td>
                  <Td align="right">₹{f2(taxData.cgstCruise)}</Td>
                  <Td align="center">{taxData.cgstCruisePct}%</Td>
                  <Td align="right">₹{f2(taxData.sgstCruise)}</Td>
                  <Td align="right" bold>₹{f2(taxData.cgstCruise + taxData.sgstCruise)}</Td>
                </tr>
                <tr>
                  <Td shade>Food &amp; Beverage</Td>
                  <Td align="right" shade>₹{f2(taxData.taxableFood)}</Td>
                  <Td align="center" shade>{taxData.cgstFoodPct}%</Td>
                  <Td align="right" shade>₹{f2(taxData.cgstFood)}</Td>
                  <Td align="center" shade>{taxData.cgstFoodPct}%</Td>
                  <Td align="right" shade>₹{f2(taxData.sgstFood)}</Td>
                  <Td align="right" bold shade>₹{f2(taxData.cgstFood + taxData.sgstFood)}</Td>
                </tr>
              </>
            ) : (
              <tr>
                <Td>Cruise Charges</Td>
                <Td align="right">₹{f2(taxData.taxable)}</Td>
                <Td align="center">{taxData.cgstPct}%</Td>
                <Td align="right">₹{f2(taxData.cgst)}</Td>
                <Td align="center">{taxData.cgstPct}%</Td>
                <Td align="right">₹{f2(taxData.sgst)}</Td>
                <Td align="right" bold>₹{f2(taxData.cgst + taxData.sgst)}</Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Totals ── */}
      <div style={{ padding: '14px 22px', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 300 }}>
          <TotalRow label="Taxable Amount" value={f2(taxData.taxableTotal)} />
          <TotalRow label="Total CGST"     value={f2(totalCgst)} />
          <TotalRow label="Total SGST"     value={f2(totalSgst)} />
          <TotalRow label="Total Tax"      value={f2(taxData.totalTax)} />
          <div style={{ borderTop: `2px solid ${GOLD}`, marginTop: 6, paddingTop: 6 }}>
            <TotalRow label="Grand Total" value={fI(taxData.grandTotal)} big />
          </div>
          {hasTac && (
            <div style={{ borderTop: '1px dashed #d8d0c0', marginTop: 10, paddingTop: 10 }}>
              <div style={{ fontSize: 8.5, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Deductions</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0ebe0' }}>
                <span style={{ fontSize: 9.5, color: '#5a4a38', fontWeight: 600 }}>Less: Agent Commission (TAC)</span>
                <span style={{ fontSize: 9.5, color: '#c0392b', fontWeight: 700 }}>-₹{fI(tac)}</span>
              </div>
              <div style={{ borderTop: `2px solid ${GOLD}`, marginTop: 6, paddingTop: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                  <span style={{ fontSize: 12, color: NAVY, fontWeight: 800 }}>Net Amount Payable</span>
                  <span style={{ fontSize: 13, color: GOLD, fontWeight: 800 }}>₹{fI(netAmount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Amount in words */}
      <div style={{ padding: '0 22px 16px' }}>
        <div style={{ background: '#f8f6f1', border: '1px solid #e8e0d0', borderRadius: 6, padding: '8px 14px', fontSize: 10, color: NAVY }}>
          <span style={{ fontWeight: 700 }}>Amount in words: </span>
          <span style={{ fontStyle: 'italic' }}>{amountToWords(taxData.grandTotal)}</span>
        </div>
      </div>

      <GoldDivider />

      {/* ── Footer ── */}
      <div style={{ padding: '14px 22px', textAlign: 'center' }}>
        <div style={{ fontSize: 9, color: '#9a8e7a', marginBottom: 4 }}>This is a computer generated invoice</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 3 }}>Thank you for choosing Alfresco Grand</div>
        <div style={{ fontSize: 9, color: '#9a8e7a' }}>{companyName} | GSTIN: {gstin}</div>
      </div>
    </div>
  );
});

// ── Modal wrapper with print button ──────────────────────────────────────────
export default function InvoicePrint({ invoiceData, onClose }) {
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4 portrait; margin: 0; } body { margin: 0; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
    documentTitle: invoiceData.invoiceNumber,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(10,22,40,0.75)',
      display: 'flex', flexDirection: 'column',
      zIndex: 2100, overflow: 'hidden',
    }}>
      {/* Control bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px', background: NAVY, flexShrink: 0,
      }}>
        <div>
          <div style={{ color: GOLD, fontWeight: 800, fontSize: '0.95rem' }}>{invoiceData.invoiceNumber}</div>
          <div style={{ color: '#b0c4d8', fontSize: '0.78rem', marginTop: 1 }}>{invoiceData.customerName}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handlePrint}
            style={{
              background: GOLD, color: NAVY, border: 'none', borderRadius: 8,
              padding: '8px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            🖨 Print / Save PDF
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 8, padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Scrollable preview */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '24px 16px', background: '#3a3a4a' }}>
        <div style={{ background: '#ffffff', boxShadow: '0 8px 40px rgba(0,0,0,0.5)', width: '210mm', maxWidth: '100%' }}>
          <InvoiceContent ref={contentRef} invoiceData={invoiceData} />
        </div>
      </div>
    </div>
  );
}
